import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    Method,
} from 'axios';
import { getStoredLanguage } from 'shared/i18n';
import { clearAuthToken, getAuthToken } from './tokenStorage';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.cyberfit.pro';
export const authSessionExpiredEvent = 'auth-session-expired';

export class ApiError<T = unknown> extends Error {
    constructor(
        message: string,
        public readonly status?: number,
        public readonly data?: T,
        public readonly cause?: unknown,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

export const buildApiHeaders = (
    headers?: AxiosRequestConfig['headers'] | HeadersInit,
    includeJsonContentType = true,
) => {
    const merged: Record<string, string> = {
        'X-App-Language': getStoredLanguage(),
    };
    if (includeJsonContentType) merged['Content-Type'] = 'application/json';

    if (headers) {
        if (typeof Headers !== 'undefined' && headers instanceof Headers) {
            headers.forEach((value, key) => {
                merged[key] = value;
            });
        } else if (Array.isArray(headers)) {
            headers.forEach(([key, value]) => {
                merged[key] = String(value);
            });
        } else {
            Object.entries(headers as Record<string, unknown>).forEach(([key, value]) => {
                if (value !== undefined && value !== null) merged[key] = String(value);
            });
        }
    }

    const token = getAuthToken();
    if (token && !merged.Authorization) merged.Authorization = `Bearer ${token}`;
    return merged;
};

const normalizePath = (path: string) => `/${path.replace(/^\/+/, '')}`;

const getErrorMessage = (error: AxiosError<unknown>) => {
    const data = error.response?.data;
    if (data && typeof data === 'object') {
        const record = data as Record<string, unknown>;
        const message = record.error ?? record.message;
        if (typeof message === 'string' && message.trim()) return message;
    }
    return error.message || 'Network request failed';
};

export const apiClient: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 20_000,
});

apiClient.interceptors.request.use((config) => {
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    config.headers = buildApiHeaders(config.headers, !isFormData) as any;
    return config;
});

apiClient.interceptors.response.use(
    (response) => response,
    (error: AxiosError<unknown>) => {
        if (error.response?.status === 401) {
            clearAuthToken();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent(authSessionExpiredEvent));
            }
        }
        return Promise.reject(new ApiError(
            getErrorMessage(error),
            error.response?.status,
            error.response?.data,
            error,
        ));
    },
);

export async function apiRequest<T = any>(
    path: string,
    method: Method,
    data?: any,
    options: AxiosRequestConfig = {},
): Promise<T> {
    const response = await apiClient.request<T>({
        ...options,
        url: normalizePath(path),
        method,
        data,
    });
    return response.data;
}

export const apiGet = <T = any>(path: string, options?: AxiosRequestConfig) =>
    apiRequest<T>(path, 'get', undefined, options);

export const apiPost = <T = any>(path: string, data?: any, options?: AxiosRequestConfig) =>
    apiRequest<T>(path, 'post', data, options);

export const apiDelete = <T = any>(path: string, data?: any, options?: AxiosRequestConfig) =>
    apiRequest<T>(path, 'delete', data, options);
